import React from 'react';
import type { ClassNameProp, PatientDocumentProps, ToolProps } from '..';
import { Direction, Drawer, PatientDocument } from '..';
import { Tool } from '../basic';
import { intl } from '../../lib';
import { reactNodeIsOfType } from '../../util';

type PatientDocumentActionsDrawerProps = {
  /**
   * Actions to be displayed in the document, actions are expected to be a list of JSX elements (i.e. Tools)
   * Every action also receives additional classes to style it properly - 'drawer-action', 's-font', 'font-16'
   */
  actions: React.JSX.Element[];
  /**
   * Callback to close the drawer
   * */
  onClose: VoidFunction;
  /**
   * Necessary data about the document for which drawer is opened
   */
  documentContent: PatientDocumentProps['document'];
} & ClassNameProp;
/**
 * Drawer used for displaying actions for a document
 * @return  {@link React.JSX.Element}
 *
 */
export const PatientDocumentActionsDrawer = ({
  actions,
  onClose,
  documentContent,
  className,
  ...props
}: PatientDocumentActionsDrawerProps): React.JSX.Element => {
  actions = React.Children.map(actions, tool => {
    if (!React.isValidElement(tool)) {
      throw new Error('You can only provide valid React elements as actions');
    }
    if (!reactNodeIsOfType(tool, Tool)) {
      throw new Error('You can only provide Tool components as actions');
    }

    const currentClassnames = new Set([
      ...((tool.props as ToolProps).className?.split(' ') ?? []),
      'drawer-action',
      's-font',
      'font-16'
    ]);

    return React.cloneElement(tool, {
      className: Array.from(currentClassnames).filter(Boolean).join(' ')
    } as Partial<ToolProps>);
  });

  return (
    <Drawer
      ariaLabel={documentContent.title}
      onOverlayClicked={onClose}
      direction={Direction.BOTTOM}
      className={`ehs-document-actions-drawer s-font ${className}`}
      enableFocusTrap
      roundedCorners
      {...props}
    >
      <div className="drawer-header">
        <PatientDocument
          document={documentContent}
          actions={
            <Tool
              icon="delete"
              tooltip={intl('view.button.close')}
              className="document-actions-drawer-close-button"
              onClick={onClose}
            />
          }
        />
      </div>
      <DrawerActions actions={actions} />
    </Drawer>
  );
};

function DrawerActions({
  actions
}: Readonly<Pick<PatientDocumentActionsDrawerProps, 'actions'>>) {
  return (
    <>
      {actions.map(action => (
        <div key={'action_' + action.key} className="drawer-row">
          {action}
        </div>
      ))}
    </>
  );
}
